import React, { useState, useRef } from 'react';
import { COLORS } from '../../data/siteData';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';

// YouTube videos with titles
const PRESS_VIDEOS = [
  {
    id: 'rpaAlpff9PQ',
    title: 'Bibliomatas Kaune',
    url: 'https://www.youtube.com/embed/rpaAlpff9PQ',
  },
  {
    id: 'IyGYvRtnTHA',
    title: 'Knygų atsiėmimas 24/7',
    url: 'https://www.youtube.com/embed/IyGYvRtnTHA',
  },
  {
    id: '9ZGhy0-6Yss',
    title: 'Bibliotekų inovacijos',
    url: 'https://www.youtube.com/embed/9ZGhy0-6Yss',
  },
  {
    id: 'UMCVitvz350',
    title: 'Modernios bibliotekos',
    url: 'https://www.youtube.com/embed/UMCVitvz350',
  },
  {
    id: 'YAenq-rTxBo',
    title: 'Savitarnos sprendimai',
    url: 'https://www.youtube.com/embed/YAenq-rTxBo',
  },
  {
    id: '78em1mML8FI',
    title: 'Bibliomatų pristatymas',
    url: 'https://www.youtube.com/embed/78em1mML8FI',
  },
  {
    id: 'hCVUBy4s9y8',
    title: 'Technologijos bibliotekose',
    url: 'https://www.youtube.com/embed/hCVUBy4s9y8',
  },
  {
    id: '6JOaD6n_Wvw',
    title: 'Knygomatų ateitis',
    url: 'https://www.youtube.com/embed/6JOaD6n_Wvw',
  },
];

const PressSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(null);
  const carouselRef = useRef(null);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % PRESS_VIDEOS.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + PRESS_VIDEOS.length) % PRESS_VIDEOS.length);
  };

  const handleVideoClick = (videoId) => {
    setPlayingVideo(playingVideo === videoId ? null : videoId);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
            Apie mus kalba!
          </h2>
          <p className="text-lg" style={{ color: COLORS.gray }}>
            Žiniasklaida ir partneriai apie Knygomatai® sprendimus
          </p>
        </div>

        {/* Desktop Grid - 4 columns */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-6">
          {PRESS_VIDEOS.map((video) => (
            <div key={video.id} className="group">
              <div 
                className="relative aspect-video rounded-xl overflow-hidden shadow-lg cursor-pointer bg-gray-900"
                onClick={() => handleVideoClick(video.id)}
              >
                {playingVideo === video.id ? (
                  <iframe
                    src={`${video.url}?autoplay=1`}
                    title={video.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <img
                      src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div 
                        className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ backgroundColor: COLORS.red }}
                      >
                        <Play className="w-6 h-6 text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <h3 className="mt-3 text-sm font-medium text-center" style={{ color: COLORS.dark }}>
                {video.title}
              </h3>
            </div>
          ))}
        </div>

        {/* Tablet Grid - 2 columns */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:hidden gap-6">
          {PRESS_VIDEOS.map((video) => (
            <div key={video.id} className="group">
              <div 
                className="relative aspect-video rounded-xl overflow-hidden shadow-lg cursor-pointer bg-gray-900"
                onClick={() => handleVideoClick(video.id)}
              >
                {playingVideo === video.id ? (
                  <iframe
                    src={`${video.url}?autoplay=1`}
                    title={video.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <img
                      src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div 
                        className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ backgroundColor: COLORS.red }}
                      >
                        <Play className="w-6 h-6 text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <h3 className="mt-3 text-sm font-medium text-center" style={{ color: COLORS.dark }}>
                {video.title}
              </h3>
            </div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="sm:hidden">
          <div className="relative">
            {/* Carousel Container */}
            <div 
              ref={carouselRef}
              className="overflow-hidden"
            >
              <div 
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {PRESS_VIDEOS.map((video) => (
                  <div key={video.id} className="w-full flex-shrink-0 px-2">
                    <div 
                      className="relative aspect-video rounded-xl overflow-hidden shadow-lg cursor-pointer bg-gray-900"
                      onClick={() => handleVideoClick(video.id)}
                    >
                      {playingVideo === video.id ? (
                        <iframe
                          src={`${video.url}?autoplay=1`}
                          title={video.title}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <>
                          <img
                            src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <div 
                              className="w-16 h-16 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: COLORS.red }}
                            >
                              <Play className="w-7 h-7 text-white ml-1" fill="white" />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <h3 className="mt-3 text-base font-medium text-center" style={{ color: COLORS.dark }}>
                      {video.title}
                    </h3>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
              style={{ marginTop: '-20px' }}
            >
              <ChevronLeft className="w-5 h-5" style={{ color: COLORS.dark }} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
              style={{ marginTop: '-20px' }}
            >
              <ChevronRight className="w-5 h-5" style={{ color: COLORS.dark }} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {PRESS_VIDEOS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'w-6' : ''
                }`}
                style={{ 
                  backgroundColor: index === currentIndex ? COLORS.yellow : COLORS.lightGray 
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PressSection;
